import { useEffect, useRef, useState, createContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {useParams, useLocation, useNavigate} from 'react-router-dom';
import { fetchPrograms } from 'app/store/programsSlice';
import { fetchLocations } from 'app/store/orgLocationsSlice';
import useLocationStatus from './useLocationStatus';
import ChannelApp from 'app/shared-components/channel/channelApp/ChannelApp';
import ProgramForm from './ProgramForm';
import LocationForm from './LocationForm';
import ProgramLocations from './ProgramLocations';
import LocationPrograms from './LocationPrograms';
import OrgReferralDetails from './OrgReferralDetails';
import ClientForm from "app/shared-components/client/ClientForm";
import {fetchClients} from "app/store/clientsSlice";
import OrganizationRightSidebarContent from "./OrganizationRightSidebarContent";
import {selectClientById} from 'app/store/clientsSlice';
import SidebarListItem from "app/shared-components/layout/SidebarListItem";
import {selectChannel} from "app/store/channelsSlice";
import ContextualSidebarContent from './ContextualSidebarContent';

export const OrganizationAppContext = createContext({});


function OrganizationApp(props) {
  const dispatch= useDispatch();
  const navigate = useNavigate();
  const routeParams = useParams();
  const [rightSidebarOpen, setRightSidebarOpen] = useState(false);
  const [templateActions, setTemplateActions] = useState({});
  const { groupId, clientId} = routeParams;
  const {isProgram, isLocation, isPrograms, isLocations,
      isProgramLocations, isLocationPrograms, isReferral,
      isClients, isClient, isClientForm, isTemplates, isServices} = useLocationStatus();
  const client= useSelector(selectClientById(clientId));
  const clientName=  client?.firstName + " " + client?.lastName;
    const channel= useSelector(selectChannel(groupId));
    const permissions = channel ? channel.callerPermissions : {};
    const canManage= permissions.admin || permissions.owner;

  //Extra scenarios for sidebar open
  const openRightSidebar= (rightSidebarOpen || isProgram || isLocation || isReferral || isClientForm) ? true : undefined;
  
  //Extra scenarios for header breadcrumbs
  const headerBreadcrumb=
      isPrograms ? " > Programs" :
          isLocations ? " > Locations"  :
              (isClients || isClientForm) ? " > Clients"  :
                  isClient ? (" > Client > " + clientName) :
                  isTemplates ? " > Record Templates" :
                  isServices ? " > Services" :
                  undefined;
                      
  //Extra scenarios for header buttons
  const headerButton=
      isPrograms ? { icon: "heroicons-outline:plus",  text: "New Program", link: "program/new" } :
          isLocations ? { icon: "heroicons-outline:plus",  text: "New Location", link: "location/new" } :
              isClients ? { icon: "heroicons-outline:plus",  text: "New Client", link: "clients/new" } :
                  isTemplates ? [
                      { icon: "heroicons-outline:plus", text: "New Template", onClick: templateActions.onCreateTemplate },
                      { icon: "heroicons-outline:cog", text: "Manage Types", onClick: templateActions.onManageTypes }
                  ] :
                  isServices ? { icon: "heroicons-outline:plus", text: "New Service", onClick: templateActions.onCreateService } :
                  undefined;
                  
  console.log('isTemplates:', isTemplates, 'templateActions:', templateActions);
                      
  //Extra scenarios for right sidebar content element
  const sidebarElement=
      isProgram && !isProgramLocations ? ProgramForm :
          isProgramLocations ? ProgramLocations :
              isLocation && !isLocationPrograms ? LocationForm :
                  isLocationPrograms ? LocationPrograms :
                      isReferral ? OrgReferralDetails :
                          isClientForm ? ClientForm :
                              rightSidebarOpen ? OrganizationRightSidebarContent : //Currently only notes comment panel
                              undefined;
  const sidebarElementFullHeight= rightSidebarOpen ? true : undefined; //Should only be true for notes comment panel

  console.log("rightSidebarOpen: " + rightSidebarOpen);
  console.log(sidebarElement);

  //Extra menu items
  const menuItems= [];
    menuItems.push({
        requiresAdmin: true,
        link: "/apps/organization/" + groupId + "/clients",
        key: "clients",
        icon: "material-outline:people_outline",
        text: "Clients"
    });
    if (isClient){
        menuItems.push({
            requiresAdmin: true,
            key: clientId,
            icon: "material-outline:people_outline",
            text: clientName,
            expanded: true,
            children: [
                {
                    key: 'clientNotes',
                    icon: 'heroicons-outline:pencil-square',
                    text: 'Client Notes',
                    link: "/apps/organization/" + groupId + "/manageclient/" + clientId,
                    requiresAdmin: true
                },
                {
                    key: 'clientTeams',
                    icon: 'material-outline:people_outline',
                    text: 'Client Teams',
                    link: "/apps/organization/" + groupId + "/manageclient/" + clientId + "/teams",
                    requiresAdmin: true
                },
                {
                    key: 'clientUsers',
                    icon: 'heroicons-outline:user',
                    text: 'Authorized Users',
                    link: "/apps/organization/" + groupId + "/manageclient/" + clientId + "/users",
                    requiresAdmin: true
                }
            ]
        });
    }

if (canManage) {
    menuItems.push({
        requiresAdmin: true,
        link: "/apps/organization/" + groupId + "/referrals",
        key: "referrals",
        icon: "material-outline:outbound",
        text: "Referrals"
    });
    menuItems.push({
        requiresAdmin: true,
        link: "/apps/organization/" + groupId + "/locations",
        key: "locations",
        icon: "heroicons-outline:building-office-2",
        text: "Locations"
    });
    menuItems.push({
        requiresAdmin: true,
        link: "/apps/organization/" + groupId + "/programs",
        key: "programs",
        icon: "material-outline:local_library",
        text: "Programs"
    });
    menuItems.push({
        requiresAdmin: true,
        link: "/apps/organization/" + groupId + "/templates",
        key: "templates",
        icon: "heroicons-outline:template",
        text: "Record Templates"
    });
    menuItems.push({
        requiresAdmin: true,
        link: "/apps/organization/" + groupId + "/services",
        key: "services",
        icon: "heroicons-outline:briefcase",
        text: "Services"
    });
}

  const onSidebarClose = () => {
    setRightSidebarOpen(false);
    if (isProgram || isLocation || isReferral || isClientForm) navigate(-1);
  };

  useEffect(() => {
    if (groupId && canManage) {
		dispatch(fetchPrograms({organizationId: groupId}));
		dispatch(fetchLocations({organizationId: groupId}));
        dispatch(fetchClients({channelId: groupId}));
	}
  }, [groupId, canManage]);


  return (
      <OrganizationAppContext.Provider value={{ setRightSidebarOpen, setTemplateActions }}>

	        <ChannelApp type="organization" menuItems={menuItems}
                        openRightSidebar={openRightSidebar}
                        sidebarElementFullHeight={sidebarElementFullHeight}
                        onSidebarClose={onSidebarClose}
                        headerButton={headerButton}
                        headerBreadcrumb={headerBreadcrumb}
                        sidebarElement={sidebarElement}
                        customLeftSidebar={ContextualSidebarContent}/>

      </OrganizationAppContext.Provider>
  );
}

export default OrganizationApp;

